import { LOCAL_STORAGE_KEY_JWT } from '@/api'
import { analytics } from '@/features/analytics/events'
import { useFetchWorkers } from '@/features/devices/api/useFetchWorkers'
import { useCurrentConfiguration } from '@/features/devices/api/version'
import { routes } from '@/pages'
import { Button, Text } from '@/ui-kit'
import { Dialog, DialogContent, DialogTrigger } from '@/ui-kit/components/Dialog'
import { clearUserLocalStorage, sendMessageToExtension } from '@/utils'
import { useLocalStorage } from '@/utils/useLocalStorage'
import { clearUtmParams } from '@/utils/utmParams'
import { Trans } from '@lingui/react/macro'
import { useWallet } from '@solana/wallet-adapter-react'
import { PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useCompleteSignUp } from '../api/authorize'
import { authService } from '../api/authService'
import { useFetchUser } from '../api/useFetchUser'
import { useInviteCode } from './InviteCodeProvider'

type SessionProviderValue = {
  signInWithMagicLink: (email: string) => Promise<void>
  logout: () => Promise<void>
}

const SessionProviderContext = createContext({} as SessionProviderValue)

export const SessionProvider = ({ children }: PropsWithChildren) => {
  const [showModal, setShowModal] = useState(false)
  const [jwt, setJwt] = useLocalStorage(LOCAL_STORAGE_KEY_JWT, '')
  const [isReady, setIsReady] = useState(false)
  const { data: user } = useFetchUser(!!jwt)
  const { data: config } = useCurrentConfiguration()
  const { data: workers, isFetched, refetch: refetchWorkers } = useFetchWorkers(false, !!jwt)
  const { disconnect: disconnectWallet } = useWallet()
  const { inviteCode } = useInviteCode()

  const logout = useCallback(async () => {
    try {
      analytics.clearIdentity()
      await disconnectWallet()
      await sendMessageToExtension('logout')
      localStorage.removeItem(LOCAL_STORAGE_KEY_JWT)
      clearUserLocalStorage()
      clearUtmParams()
    } catch (err) {
      console.log('Logout error: ', err)
    } finally {
      setJwt('')
      window.location.replace(routes.login.path)
    }
  }, [])

  useEffect(() => {
    const handleFocus = async () => {
      if (jwt) {
        try {
          const result = await sendMessageToExtension<{ isNewlyInstalled: boolean }>('isInstalled')
          const isNewlyInstalled = result?.isNewlyInstalled
          if (isNewlyInstalled !== undefined) {
            if (isNewlyInstalled) {
              await sendMessageToExtension<void>('login', { email: user?.email || '-', jwt: jwt })
              const webWorkersLength = workers?.filter((worker) => worker.type === 'extension').length
              setShowModal(isFetched && !webWorkersLength)
            } else {
              const isExtensionLoggedIn = await sendMessageToExtension('isLoggedIn')
              if (!isExtensionLoggedIn) {
                logout()
              }
            }
          }
        } catch (error) {
          console.error(error)
        }
      }
    }

    window.addEventListener('focus', handleFocus)
    handleFocus()

    return (): void => window.removeEventListener('focus', handleFocus)
  }, [jwt, workers, logout, user])

  const { mutateAsync: completeAuth } = useCompleteSignUp()
  useEffect(() => {
    const completeSignUp = async () => {
      if (user && !user.isSignUpCompleted) {
        await completeAuth(undefined)
      }
    }
    completeSignUp()
  }, [user, completeAuth])

  useEffect(() => {
    if (user?.id) analytics.identify(user.id)
  }, [user?.id])

  useEffect(() => {
    async function checkSession() {
      if (jwt) {
        setIsReady(true)
        if (window.location.pathname.includes(routes.login.path)) {
          window.location.replace('/')
        }
      } else {
        setIsReady(true)
      }
    }
    checkSession()
  }, [])

  const signInWithMagicLink = useCallback(
    async (email: string) => {
      await authService.requestMagicLink(email, inviteCode)
    },
    [inviteCode]
  )

  const value: SessionProviderValue = useMemo(
    () => ({
      signInWithMagicLink,
      logout
    }),
    [signInWithMagicLink, logout]
  )

  return (
    <SessionProviderContext.Provider value={value}>
      {isReady && children}
      <Dialog open={showModal} onOpenChange={(value) => setShowModal(value)}>
        <DialogTrigger />
        <DialogContent className="!max-w-[520px]">
          <img
            alt=""
            src="/images/firework-orange.png"
            className="absolute top-0 left-1/2 h-[188px] w-[188px] -translate-x-3/4 -translate-y-[80%]"
          />
          <div className="absolute top-0 left-1/3 h-[60px] w-1/2 bg-[var(--colorBlackToWhite)]" />
          <img
            alt=""
            src="/images/firework-green.png"
            className="absolute top-0 left-[47%] h-[188px] w-[188px] -translate-y-3/4"
          />
          <div className="mt-6 flex flex-col items-center">
            <Text type="h3" className="mb-2">
              <Trans>Congratulations!</Trans>
            </Text>
            <span className="max-w-[320px] text-center">
              You've earned
              <span className="text-[var(--color)]">{` ${config?.extension_installation_reward_points} points`}</span>{' '}
              for completing your first browser extension installation!
            </span>
          </div>
          <Button
            className="mt-4"
            onClick={() => {
              setShowModal(false)
              refetchWorkers()
            }}
          >
            <Trans>Start earning</Trans>
          </Button>
        </DialogContent>
      </Dialog>
    </SessionProviderContext.Provider>
  )
}

export function useSessionProvider(): SessionProviderValue {
  return useContext(SessionProviderContext)
}
